import { useTheme } from "@mui/material";
import { wait } from "@testing-library/user-event/dist/utils";
import { toast, types, customerPortal } from "@vilocnv/allsetra-core";
import { selectDrawerSelectedAccountId } from "app/data/selectors";
import { SignalRService } from "app/data/services";
import { deleteGeozoneAction } from "domain/geozones/store/actions/delete-geozone.action";
import { getGeozonesAction } from "domain/geozones/store/actions/get-geozones.action";
import { selectGeozonesState } from "domain/geozones/store/selectors/geozones.selector";
import { ObjectsLocationsRequestDto } from "domain/objects/entities/dtos/objects-locations-request.dto";
import getObjectDataForMapAction from "domain/objects/store/actions/get-object-data-for-map.action";
import getObjectsLocationsAction from "domain/objects/store/actions/get-objects-locations.action";
import {
  clearSelectedObject,
  setSelectedObject
} from "domain/objects/store/objects.slice";
import { selectObjectsState } from "domain/objects/store/selectors/objects.selector";
import { useAppDispatch, useAppSelector } from "hooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

type DeleteGeozoneRequestDto = customerPortal.geozones.DeleteGeozoneRequestDto;
type GeozonesRequestDto = customerPortal.geozones.GetGeozonesRequestDto;
type ObjectForMapRequestDto = customerPortal.objects.ObjectForMapRequestDto;

const useGeozonesPage = () => {
  const [mounted, setMounted] = useState(false);
  const [showMarkers, setShowMarkers] = useState(false);
  const [selectedTooltipMarker, setSelectedTooltipMarker] =
    useState<types.MarkerType>();
  const [searchParams] = useSearchParams();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGeozoneId, setSelectedGeozoneId] = useState<string>();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const { geozones, isLoadingGeozones } = useAppSelector(selectGeozonesState);
  const { markers, objectFilters, selectedObject, isLoadingObjectDetail } =
    useAppSelector(selectObjectsState);
  const searchQuery = searchParams.get("search");
  const filteredGeozones: Array<types.IGeozone> = useMemo(() => {
    if (!geozones || geozones.length === 0) return [];

    if (!searchQuery) return geozones;

    return geozones.filter((geozone: types.IGeozone) =>
      geozone.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    );
  }, [searchQuery, geozones]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    dispatchGetGeozones();
    dispatchGetObjects(objectFilters);
  }, [mounted, accountId]);

  const dispatchGetObjects = async (filters: types.IMapFilter) => {
    const requestData: ObjectsLocationsRequestDto = {
      accountId: accountId ?? "",
      filters: {
        ...filters
      }
    };

    return dispatch(getObjectsLocationsAction(requestData));
  };

  const dispatchGetGeozones = () => {
    const requestData: GeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getGeozonesAction(requestData));
  };

  const dispatchDeleteGeozone = async (geozoneId: string) => {
    const requestData: DeleteGeozoneRequestDto = {
      accountId: accountId ?? "",
      accountGeoFenceId: geozoneId ?? ""
    };

    return dispatch(deleteGeozoneAction(requestData));
  };

  const toggleShowMarkers = () => {
    setShowMarkers(!showMarkers);
  };

  const handleMarkerMouseOver = (uniqueId: string) => {
    setSelectedTooltipMarker(undefined);
    const marker = markers.find((m) => m?.uniqueId === uniqueId);
    if (marker && marker.position && marker.name) {
      setSelectedTooltipMarker(marker);
    }
  };

  const handleMarkerMouseOut = () => {
    setSelectedTooltipMarker(undefined);
  };

  const handleShowInfoWindowClick = (uniqueId: string) => {
    if (!uniqueId) return;

    dispatch(setSelectedObject({ uniqueId }));

    const requestData: ObjectForMapRequestDto = {
      accountId: accountId ?? "",
      objectId: uniqueId
    };
    dispatch(getObjectDataForMapAction(requestData));
  };

  const handleCloseInfoWindowClick = () => {
    dispatch(clearSelectedObject());
  };

  const onEditGeozoneClick = (geozone: types.IGeozone) => {
    navigate(`/dashboard/geozone/edit/${geozone.uniqueId}`);
  };

  const deleteGeozoneEventListener = (event: any) => {
    if (
      event.eventName === types.BackendEventsEnum.GeozoneRemovedFromAccountEvent
    ) {
      toast.success("Geozone has been deleted successfully.");

      setIsDeleting(false);
      setIsDeleteModalOpen(false);

      dispatchGetGeozones();

      SignalRService.hubConnection?.off(
        "EventRaised",
        deleteGeozoneEventListener
      );
    }
  };

  const onDeleteActionGeozoneClick = async (geozone: types.IGeozone) => {
    setSelectedGeozoneId(geozone?.uniqueId ?? "");
    setIsDeleteModalOpen(true);
  };

  const onDeleteGeozoneClick = async () => {
    try {
      SignalRService.hubConnection?.on(
        "EventRaised",
        deleteGeozoneEventListener
      );

      setIsDeleting(true);

      await dispatchDeleteGeozone(selectedGeozoneId ?? "");

      await wait(1000);
    } finally {
      setIsDeleteModalOpen(false);
      setIsDeleting(false);
    }
  };

  const onAddGeozoneButtonClick = () => {
    navigate(`/dashboard/geozone/create`);
  };

  const onCloseDeleteModalClick = () => {
    setIsDeleteModalOpen(false);
    setSelectedGeozoneId(undefined);
  };

  const onGeozoneItemClick = (geozoneUniqueId: string) => {
    navigate(`/dashboard/geozone/${geozoneUniqueId}`);
  };

  return {
    geozones: filteredGeozones,
    isLoadingGeozones,
    markers,
    translator: t,
    theme,
    navigate,
    selectedTooltipMarker,
    selectedObject,
    onEditGeozoneClick,
    onDeleteGeozoneClick,
    onAddGeozoneButtonClick,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
    handleShowInfoWindowClick,
    handleCloseInfoWindowClick,
    showMarkers,
    toggleShowMarkers,
    isLoadingObjectDetail,
    onGeozoneItemClick,
    isDeleting,
    isDeleteModalOpen,
    onCloseDeleteModalClick,
    onDeleteActionGeozoneClick
  };
};

export default useGeozonesPage;
